/**
 * Générateur — Automatisme : placer sur une droite graduée un point dont
 * l'abscisse est un nombre relatif.
 * Attendu ID : 1112 | Niveau : 4ème | Domaine : Repérage
 * Rubrique : AUTOMATISME (programme 2026, Repérage sur une droite et dans le plan 4e).
 * Labels (cartes exerciseur) :
 *   - Placer le point                (interactif placer_points : deux abscisses, dont une négative ;
 *                                     QCM sur droite graduée : quatre points, un seul a l'abscisse demandée)
 *   - Vrai ou faux ?                 (V/F sur droite graduée sans points : sens, nombre de graduations, entre quelles
 *                                     graduations, ordre de deux abscisses, valeur d'une graduation)
 *   - La droite graduée : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Repérage 4e + grille P1-P16.
 * A. P1 : CM2 introduit (droite graduée, décimaux positifs), 6e consolidé
 * (demi-droite graduée, décimaux), 5e étendu (nombres relatifs, repérage sur
 * une droite graduée), 4e automatisme. P2 : sens installés — graduation,
 * pas, lecture d'un décimal entre deux repères ; sens nouveau (5e) — le côté
 * gauche de 0, le signe, les opposés à égale distance de 0. Distracteurs :
 * signe ignoré (−3 placé à droite), sens de comptage inversé dans les
 * négatifs (−2,3 lu −1,7), pas pris pour 1 (pas 0,5 ou 2), −2,5 confondu avec
 * −2,05. P3 : entiers ≤ 12 en valeur absolue, décimaux à UNE décimale, pas
 * 0,1 / 0,5 / 1 / 2 (5e : décimaux à une ou deux décimales). P4 : sans objet.
 * B. P5 : droite graduée native (élément numberline), repères étiquetés, pas
 * lisible. P6 : aucun instrument. P7 : pas de valeur absolue formalisée,
 * pas de « distance à zéro » en calcul. P8 : abscisse, origine, graduation,
 * opposé, signe.
 * C. P9 : lecture d'une graduation décimale (6e, 1064-1065), signe d'un
 * relatif (5e). P10 : constat. P11 : le côté gauche de 0 compte « à
 * l'envers » (−2,3 est à gauche de −2) ; le pas n'est pas toujours 1. P12 :
 * amont direct en 5e seulement (relatifs), rémanence courte : un rappel V/F.
 * D. P13 : erreurs → leurres : opposé (signe ignoré), ± un pas (graduation
 * voisine), symétrique par rapport au repère voisin (comptage inversé), pas
 * pris pour 1 (−3 placé à −3 graduations quand le pas est 2 ou 0,5). P14 :
 * aucun calcul, comptage de graduations ≤ 10 depuis un repère. P15 : le point
 * se pose SUR une graduation (grille de snap), l'abscisse demandée est sur la
 * grille, un seul candidat porte l'abscisse demandée. P16 : bonne_reponse au
 * format canonique du composant (virgule française, sans « + », sans zéro
 * final) ; QCM à options A-D dans l'ordre (l'étiquette du point EST l'option).
 * INVARIANTS : arithmétique en DIXIÈMES entiers ; ≤ 60 graduations ; jamais
 * un point sur une borne ni sur un repère étiqueté ; au moins une abscisse
 * négative par exercice ; V/F sans crochets, mixité 1-3 faux, familles
 * exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, basePayload,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1112;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PLACER = 'Placer le point';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'La droite graduée : vrai ou faux ?';

// ─── Écritures (dixièmes entiers → texte) ──────────────────────────────────
const fr = (d) => { const s = d < 0 ? '-' : ''; const a = Math.abs(d); return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`); };
const signe = (d) => (d > 0 ? `+${fr(d)}` : fr(d));                 // affichage signé (« +2,5 », « -3 », « 0 »)
const val = (d) => d / 10;                                          // valeur numérique pour le composant

// ─── Régimes de graduation (dixièmes) ───────────────────────────────────────
// start / end : bornes ; pas : écart entre deux graduations ; reperes : graduations étiquetées (en dixièmes)
const REGIMES = {
    entiers: () => ({ nom: 'entiers', start: -60, end: 60, pas: 10, reperes: [0, 10] }),
    pas2: () => ({ nom: 'pas2', start: -120, end: 120, pas: 20, reperes: [0, 20] }),
    demis: () => ({ nom: 'demis', start: -40, end: 40, pas: 5, reperes: [0, 10] }),
    dixiemes: () => { const c = -randInt(1, 4) * 10; return { nom: 'dixiemes', start: c - 10, end: c + 10, pas: 1, reperes: [c - 10, c, c + 10] }; },
    dixiemes0: () => ({ nom: 'dixiemes0', start: -15, end: 15, pas: 1, reperes: [-10, 0, 10] }),
};
const tirerRegime = () => REGIMES[randomChoice(Object.keys(REGIMES))]();
/** Graduations disponibles : ni les bornes, ni les repères étiquetés. */
const grille = (reg) => { const g = []; for (let d = reg.start + reg.pas; d <= reg.end - reg.pas; d += reg.pas) if (!reg.reperes.includes(d)) g.push(d); return g; };
const dans = (reg, d) => d > reg.start && d < reg.end && (d - reg.start) % reg.pas === 0;
const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const numberline = (reg, points = []) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: val(reg.start), end: val(reg.end), step: val(reg.pas), reperes: reg.reperes.map(val),
            lineType: 'full', showStart: false, showEnd: false,
            points: points.map((p) => ({ value: val(p.d), label: p.label, showValue: false })),
        },
    }],
});

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** Les positions où une erreur placerait d (dixièmes) ; comptage_inverse classique seul (−2,7 en −1,3), pas pris pour 1 aux pas 2 et 0,5 seuls. */
const erreursPlacement = (reg, d) => {
    const e = [{ code: 'mauvais_cote_de_zero', d: -d }];
    if (d < 0 && d % 10 !== 0) e.push({ code: 'comptage_inverse', d: 2 * (d - (d % 10)) - d });
    e.push({ code: 'traits_au_lieu_d_intervalles', d: d - reg.pas }, { code: 'traits_au_lieu_d_intervalles', d: d + reg.pas });
    if (reg.pas === 20) e.push({ code: 'pas_pris_pour_1', d: 2 * d });
    if (reg.pas === 5 && d % 10 === 0) e.push({ code: 'pas_pris_pour_1', d: d / 2 });
    return e.filter((x) => x.d !== d);
};
/** Le code d'une position, s'il est seul à y mener (null sinon). */
const codeEn = (erreurs, v) => { const c = [...new Set(erreurs.filter((x) => x.d === v).map((x) => x.code))]; return c.length === 1 ? c[0] : null; };
/** Le code de « x est entre lo et hi » (faux) : la seule erreur qui place x dans cette fenêtre. */
const codeFenetre = (erreurs, lo, hi) => { const c = [...new Set(erreurs.filter((x) => x.d > lo && x.d < hi).map((x) => x.code))]; return c.length === 1 ? c[0] : null; };
/** « on compte n graduations » (faux) : une de trop ou de moins, les unités comptées comme des graduations, ou le compte fait depuis 0. */
const codeComptage = (n, nGrad, unites, depuisOrigine) => {
    const c = new Set();
    if (n === nGrad + 1 || n === nGrad - 1) c.add('traits_au_lieu_d_intervalles');
    if (unites !== null && n === unites) c.add('pas_pris_pour_1');
    if (depuisOrigine !== null && n === depuisOrigine) c.add('origine_obligatoire');
    return c.size === 1 ? [...c][0] : null;
};
/** « a est à droite de b » (faux) rangé comme les distances à zéro : deux négatifs, ou deux signes contraires. */
const codeOrdre = (a, b, ditPlusGrand) => {
    if (Math.abs(a) === Math.abs(b) || (Math.abs(a) > Math.abs(b)) !== ditPlusGrand) return null;
    if (a < 0 && b < 0) return 'ordre_des_absolues_non_inverse';
    return a * b < 0 ? 'mauvais_cote_de_zero' : null;
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refP = { v: null }; const refQ = { v: null };

// ─── Carte 1 : placer le point (interactif) ────────────────────────────────

const construirePlacer = () => {
    const reg = tirerRegime(); const g = grille(reg);
    const negatifs = g.filter((d) => d < 0);
    // deux abscisses distinctes, dont au moins une négative
    // au moins deux graduations d'écart entre les deux points (revue 2026-09-10 : 15 % de points adjacents, étiquettes gênées)
    const d1 = randomChoice(negatifs); const loin = g.filter((d) => Math.abs(d - d1) >= 2 * reg.pas); const d2 = randomChoice(loin.length ? loin : g.filter((d) => d !== d1));
    const vals = shuffle([d1, d2]); const lettres = shuffle(LETTRES).slice(0, 2);
    const bonne = {}; vals.forEach((d, i) => { bonne[lettres[i]] = fr(d); });
    const consigne = vals.map((d, i) => `${lettres[i]}(${signe(d)})`).join(' et ');
    const payload = basePayload('association',
        `<p>Place les points ${consigne} sur la droite graduée.</p>`,
        {
            subType: 'placer_points',
            numberline: { start: val(reg.start), end: val(reg.end), step: val(reg.pas), reperes: reg.reperes.map(val), lineType: 'full', showStart: false, showEnd: false },
            pointsToPlace: lettres,
            bonne_reponse: JSON.stringify(bonne),
            sousType: reg.nom,
        },
        opts(L_PLACER));
    payload._meta.params = { regime: reg.nom, vals, signature: `${reg.nom}-${reg.start}-${vals.slice().sort().join(',')}` };
    // positions fausses nommées par les calculs des leurres du QCM, sur la droite, jamais à la place de l'autre point
    const erreurs = [];
    vals.forEach((d, i) => {
        const E = erreursPlacement(reg, d);
        [...new Set(E.map((x) => x.d))].forEach((v) => {
            const code = codeEn(E, v);
            if (code && v >= reg.start && v <= reg.end && !vals.includes(v)) erreurs.push({ code, point: lettres[i], valeur: fr(v) });
        });
    });
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genPlacer = () => antiRepeat(construirePlacer, refP);

// ─── Carte 1b : le bon point (QCM, rattaché à « Placer le point », prof 2026-09-10) ─

const construireBon = () => {
    // régime tiré UNE fois (revue 2026-09-10 : retiré à chaque échec, les dixièmes autour de 0 sortaient 42 % du temps, le pas 2 : 9 %)
    const reg = tirerRegime(); const g = grille(reg);
    for (let essai = 0; essai < 100; essai++) {
        // cible négative trois fois sur quatre, positive sinon (variété : 46 signatures avec les seules négatives)
        const signeCible = Math.random() < 0.75 ? -1 : 1;
        const cible = randomChoice(g.filter((d) => d * signeCible > 0));
        // repère le plus proche (pour le comptage inversé : symétrique de la cible par rapport à ce repère)
        const repere = reg.reperes.reduce((best, r) => (Math.abs(r - cible) < Math.abs(best - cible) ? r : best), reg.reperes[0]);
        const cands = [
            { d: cible, role: 'cible' },
            { d: -cible, role: 'oppose' },                                   // signe ignoré
            { d: 2 * repere - cible, role: 'comptage_inverse' },              // compté dans l'autre sens depuis le repère
            { d: cible + randomChoice([-1, 1]) * reg.pas, role: 'voisin' },   // une graduation à côté
            // pas pris pour 1 : « −6 » posé 6 graduations plus loin quand le pas est 2 (→ −12), « −3 » posé 3 graduations quand le pas est 0,5 (→ −1,5)
            { d: reg.pas === 20 ? 2 * cible : reg.pas === 5 ? cible / 2 : cible - 2 * reg.pas, role: 'pas_pris_pour_1' },
        ];
        const pris = [];
        cands.forEach((c) => { if (pris.length < 4 && dans(reg, c.d) && !reg.reperes.includes(c.d) && !pris.some((q) => q.d === c.d)) pris.push(c); });
        if (pris.length < 4) continue;
        const lettres = shuffle(['A', 'B', 'C', 'D']);
        const points = pris.map((c, i) => ({ ...c, label: lettres[i] }));
        const bonne = points.find((c) => c.role === 'cible').label;
        const payload = makeQcm(['A', 'B', 'C', 'D'], bonne, `<p>Quel point a pour abscisse ${signe(cible)} ?</p>`, { ...opts(L_PLACER), elements_additionnels: numberline(reg, points) });
        payload.contenu.choix = ['A', 'B', 'C', 'D']; payload.contenu.bonne_reponse = bonne;      // l'étiquette du point EST l'option
        payload.contenu = { ...payload.contenu, sousType: reg.nom };
        payload._meta.params = { regime: reg.nom, cible, points: points.map((c) => ({ label: c.label, role: c.role, d: c.d })), signature: `${reg.nom}-${reg.start}-${cible}` };
        // chaque point leurre nommé par l'erreur qui y place la cible, quand elle est seule à y mener
        const E = erreursPlacement(reg, cible);
        const erreurs = points.filter((c) => c.role !== 'cible').map((c) => ({ code: codeEn(E, c.d), valeur: c.label })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1112 : QCM sans candidats');
};
const genBon = () => antiRepeat(construireBon, refQ);

// ─── Carte 3 : vrai ou faux ? (sur la droite graduée) ──────────────────────

let derniereEmpreinteVf1112 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const reg = tirerRegime(); const g = grille(reg);
        const vals = shuffle(g).slice(0, 5); if (vals.length < 5) continue;
        if (!vals.some((d) => d < 0)) vals[0] = randomChoice(g.filter((d) => d < 0));
        const [d1, d2, d3, d4, d5] = vals;
        // Toutes les familles portent sur le PLACEMENT d'une abscisse donnée sur cette droite (prof, 2026-09-10) :
        // le sens depuis l'origine, le nombre de graduations depuis le repère le plus proche, entre quelles
        // graduations, l'ordre de deux abscisses à placer, la valeur d'une graduation.
        const repere = reg.reperes.reduce((best, r) => (Math.abs(r - d2) < Math.abs(best - d2) ? r : best), reg.reperes[0]);
        const nGrad = Math.abs(d2 - repere) / reg.pas; const versGauche = d2 < repere;
        const depuis = repere === 0 ? 'de l\'origine' : `de la graduation ${signe(repere)}`;
        const nGradFaux = randomChoice([nGrad + 1, nGrad - 1, reg.pas !== 10 ? Math.abs(d2 - repere) / 10 : nGrad + 2].filter((n) => Number.isInteger(n) && n >= 1 && n !== nGrad));
        const unite = reg.pas >= 10 ? 20 : 10;
        const bas = d3 % unite === 0 ? d3 - unite : Math.floor(d3 / unite) * unite;                  // d3 entier : fenêtre (d3 − u ; d3 + u), sinon (plancher ; plancher + u)
        const haut = d3 % unite === 0 ? d3 + unite : bas + unite;
        const decal = randomChoice([-1, 1]) * (haut - bas);
        const pasFaux = randomChoice([reg.pas * 2, reg.pas / 2, reg.pas === 10 ? 20 : 10].filter((p) => Number.isInteger(p) && p !== reg.pas));
        const familles = [
            { f: 'sens', vrai: `Pour placer le point d'abscisse ${signe(d1)}, on part de l'origine et on va vers ${d1 < 0 ? 'la gauche' : 'la droite'}.`, faux: `Pour placer le point d'abscisse ${signe(d1)}, on part de l'origine et on va vers ${d1 < 0 ? 'la droite' : 'la gauche'}.` },
            { f: 'graduations', vrai: `Pour placer le point d'abscisse ${signe(d2)}, on part ${depuis} et on compte ${nGrad} graduation${nGrad > 1 ? 's' : ''} vers ${versGauche ? 'la gauche' : 'la droite'}.`, faux: nGradFaux === undefined ? null : `Pour placer le point d'abscisse ${signe(d2)}, on part ${depuis} et on compte ${nGradFaux} graduation${nGradFaux > 1 ? 's' : ''} vers ${versGauche ? 'la gauche' : 'la droite'}.` },
            { f: 'entre', vrai: `Le point d'abscisse ${signe(d3)} se place entre les graduations ${signe(bas)} et ${signe(haut)}.`, faux: `Le point d'abscisse ${signe(d3)} se place entre les graduations ${signe(bas + decal)} et ${signe(haut + decal)}.` },
            { f: 'ordre', vrai: `Le point d'abscisse ${signe(d4)} se place ${d4 < d5 ? 'à gauche' : 'à droite'} du point d'abscisse ${signe(d5)}.`, faux: `Le point d'abscisse ${signe(d4)} se place ${d4 < d5 ? 'à droite' : 'à gauche'} du point d'abscisse ${signe(d5)}.` },
            { f: 'pas', vrai: `Sur cette droite, deux graduations voisines sont séparées de ${fr(reg.pas)}.`, faux: pasFaux === undefined ? null : `Sur cette droite, deux graduations voisines sont séparées de ${fr(pasFaux)}.` },
        ];
        const retenues = shuffle(familles).slice(0, 4);
        const vraiesPossibles = retenues.filter((fa) => fa.vrai).length; const faussesPossibles = retenues.filter((fa) => fa.faux).length;
        const minFaux = Math.max(1, 4 - vraiesPossibles); const maxFaux = Math.min(3, faussesPossibles); if (minFaux > maxFaux) continue;
        const nbFaux = randInt(minFaux, maxFaux);
        const ordre = shuffle(retenues); const lignes = []; let restantsFaux = nbFaux;
        ordre.forEach((fa, i) => {
            const peutFaux = !!fa.faux; const peutVrai = !!fa.vrai; const restantsLignes = ordre.length - i;
            const auFaux = peutFaux && restantsFaux > 0 && (!peutVrai || restantsFaux >= restantsLignes || Math.random() < 0.5);
            if (auFaux) { lignes.push({ l: fa.faux, v: false, f: fa.f }); restantsFaux--; } else lignes.push({ l: fa.vrai, v: true, f: fa.f });
        });
        if (lignes.some((it) => !it.l) || lignes.filter((it) => !it.v).length !== nbFaux) continue;
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1112) continue;
        derniereEmpreinteVf1112 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>Observe la droite graduée. Pour chaque affirmation sur le placement d\'un point, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: numberline(reg, []) },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { regime: reg.nom, start: reg.start, pas: reg.pas, vals, nbFaux };
        // l'idée fausse de chaque affirmation fausse, famille par famille
        const codeFaux = {
            sens: 'mauvais_cote_de_zero',
            graduations: codeComptage(nGradFaux, nGrad, reg.pas === 20 || reg.pas === 5 ? Math.abs(d2 - repere) / 10 : null, repere !== 0 ? Math.abs(d2) / reg.pas : null),
            entre: codeFenetre(erreursPlacement(reg, d3), bas + decal, haut + decal),
            ordre: codeOrdre(d4, d5, d4 < d5),
            pas: pasFaux === 10 ? 'pas_pris_pour_1' : null,
        };
        const erreurs = melange.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1112 : V/F sans tirage après 160 essais');
};

// ─── Carte 4 : la droite graduée, vrai ou faux ? (pur rappel) ──────────────

const POOL_RAPPEL = [
    { l: 'L\'abscisse de l\'origine est 0.', v: true, f: 'origine' },
    { l: 'L\'abscisse de l\'origine est 1.', v: false, f: 'origine' },
    { l: 'Un point d\'abscisse négative est à gauche de l\'origine.', v: true, f: 'gauche' },
    { l: 'Un point d\'abscisse négative est à droite de l\'origine.', v: false, f: 'gauche', code: 'mauvais_cote_de_zero' },
    { l: 'Quand l\'origine n\'est pas écrite, on part de la graduation la plus proche dont on connaît l\'abscisse.', v: true, f: 'repere' },
    { l: 'On ne peut placer un point que si l\'origine est écrite sur la droite graduée.', v: false, f: 'repere', code: 'origine_obligatoire' },
    { l: 'Sur une droite graduée, plus on va vers la gauche, plus les abscisses diminuent.', v: true, f: 'ordre' },
    { l: 'Le point d\'abscisse −5 est à droite du point d\'abscisse −2.', v: false, f: 'ordre', code: 'ordre_des_absolues_non_inverse' },
    { l: 'Le point d\'abscisse −2,5 est entre les points d\'abscisses −3 et −2.', v: true, f: 'decimal' },
    { l: 'Le point d\'abscisse −2,5 est entre les points d\'abscisses −2 et −1.', v: false, f: 'decimal', code: 'comptage_inverse' },
    { l: 'Quand deux graduations voisines sont séparées de 0,5, il faut deux graduations pour avancer de 1.', v: true, f: 'pas' },
    { l: 'Deux graduations voisines sont toujours séparées de 1.', v: false, f: 'pas', code: 'pas_pris_pour_1' },
    { l: 'Pour placer −4, on part de l\'origine et on compte 4 unités vers la gauche.', v: true, f: 'geste' },
    { l: 'Pour placer −4, on part de l\'origine et on compte 4 unités vers la droite.', v: false, f: 'geste', code: 'mauvais_cote_de_zero' },
];

let dernieresLignesRappel1112 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1112 = [];
        if (lignes.some((it) => dernieresLignesRappel1112.includes(it.l))) continue;
        dernieresLignesRappel1112 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur la droite graduée, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour « l'abscisse de l'origine est 1 »)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1112 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    placer_placer_points: genPlacer,
    placer_qcm_unique: genBon,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { placer_points: 'Placer le point', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    placer_qcm_unique: ['mauvais_cote_de_zero', 'traits_au_lieu_d_intervalles', 'comptage_inverse', 'pas_pris_pour_1'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { REGIMES, grille, fr, signe, POOL_RAPPEL };
